/**
 * Reading a chapter out of a file: the parts with no React in them.
 *
 * Separated so they can be tested directly, the way internal/narration's pure
 * logic is. Encoding detection is the reason this file exists — it is real
 * logic with a real failure mode, not glue around an input element.
 */
import type { EmphasisRange } from "../api";

/** The server's per-chapter ceiling (catalog.MaxChapterChars), counted in runes. */
export const MAX_CHAPTER_CHARS = 400_000;

/**
 * decode turns the file's bytes into text.
 *
 * `File.text()` always decodes as UTF-8, which is right for almost everything
 * and silently wrong for the rest. A Persian .txt saved out of an older Windows
 * editor is usually windows-1256, and UTF-8-decoding it yields a page of U+FFFD
 * rather than an error — so the failure arrives as a chapter of replacement
 * characters that narrates as nonsense.
 *
 * So: decode strictly as UTF-8 and, if that throws, fall back through the
 * encodings Persian text actually turns up in. A BOM wins over all of it.
 *
 * Takes bytes rather than a File so it is a pure function — reading the file is
 * the caller's job. That is also the only way to test it: jsdom's File has no
 * arrayBuffer().
 */
export function decode(bytes: Uint8Array): { text: string; encoding: string } {
  // UTF-16 byte-order marks. TextDecoder handles the rest once told which.
  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    return { text: new TextDecoder("utf-16le").decode(bytes.subarray(2)), encoding: "UTF-16 LE" };
  }
  if (bytes[0] === 0xfe && bytes[1] === 0xff) {
    return { text: new TextDecoder("utf-16be").decode(bytes.subarray(2)), encoding: "UTF-16 BE" };
  }
  // UTF-8 BOM: strip it, or it becomes an invisible first character of the
  // chapter and lands in the content hash.
  const body =
    bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf ? bytes.subarray(3) : bytes;

  try {
    return { text: new TextDecoder("utf-8", { fatal: true }).decode(body), encoding: "UTF-8" };
  } catch {
    for (const label of ["windows-1256", "windows-1252"]) {
      try {
        return { text: new TextDecoder(label, { fatal: true }).decode(body), encoding: label };
      } catch {
        // Try the next one.
      }
    }
    // Last resort: lossy UTF-8, which at least shows the user something.
    return { text: new TextDecoder("utf-8").decode(body), encoding: "UTF-8 (with errors)" };
  }
}

/** titleFrom turns "فصل-اول.txt" into "فصل اول". */
export function titleFrom(filename: string): string {
  return filename
    .replace(/\.(txt|md|markdown|text)$/i, "")
    .replace(/[_-]+/g, " ")
    .trim();
}

/**
 * fromMarkdown turns a Markdown file's `*…*` into emphasis beside the text
 * (ADR-0205): the asterisks leave the body, and each stretch they held becomes
 * a range of runes on its line. Only a pair on one line counts, opened by an
 * asterisk before a non-space and closed by one after a non-space, so a "* * *"
 * divider, a "* item" list and a lone asterisk stay as written. A doubled `**`
 * (bold) is left alone: bold is not read yet.
 */
export function fromMarkdown(text: string): { body: string; emphasis: EmphasisRange[] } {
  const emphasis: EmphasisRange[] = [];
  const lines = text.split("\n").map((line, index) => {
    const chars = Array.from(line);
    const out: string[] = [];
    let open = -1; // where the open stretch starts in out, in runes
    let openAt = -1; // where its asterisk sits in out
    const isStar = (i: number) => chars[i] === "*";
    for (let i = 0; i < chars.length; i++) {
      const ch = chars[i]!;
      const lone = ch === "*" && !isStar(i - 1) && !isStar(i + 1);
      if (lone && open < 0 && i + 1 < chars.length && !/\s/u.test(chars[i + 1]!)) {
        openAt = out.length;
        out.push(ch);
        open = out.length;
        continue;
      }
      if (lone && open >= 0 && !/\s/u.test(chars[i - 1]!) && out.length > open) {
        out.splice(openAt, 1);
        emphasis.push({ line: index, start: open - 1, end: out.length });
        open = -1;
        continue;
      }
      out.push(ch);
    }
    return out.join("");
  });
  return { body: lines.join("\n"), emphasis };
}
